import type { CatalogItemDto, HomeStageOfferDto, PropertyPublic, RealEstateListing } from "@townsy/contracts";
import type { CheckoutRequestInput } from "./checkoutRequest";
import { isDemoStore } from "./gates";

/**
 * What each dollar button asks the checkout to price. Only identities and the version the buyer
 * looked at travel to the server; the quote decides the price, the funding and the eligibility.
 */

export function commercialCheckout(p: Pick<PropertyPublic, "id" | "address" | "version" | "ownershipIntervalId" | "status" | "currentBasisCents" | "placement">, name: string): CheckoutRequestInput {
  const buy = p.ownershipIntervalId === null || p.status === "AVAILABLE" || isDemoStore(p);
  return {
    mode: "new",
    target: { kind: "COMMERCIAL_ACQUISITION", propertyId: p.id, expectedPropertyVersion: p.version },
    subject: { title: `${buy ? "Buy" : "Take over"} ${name}`, kind: "COMMERCIAL_ACQUISITION", propertyId: p.id, address: p.address },
  };
}

export function plotCheckout(listing: Pick<RealEstateListing, "propertyId" | "address">): CheckoutRequestInput {
  return {
    mode: "new",
    target: { kind: "RESIDENTIAL_PLOT", propertyId: listing.propertyId },
    subject: { title: `Buy the plot at ${listing.address}`, kind: "RESIDENTIAL_PLOT", propertyId: listing.propertyId, address: listing.address },
  };
}

export function upgradeCheckout(home: { propertyId: string; address: string }, next: Pick<HomeStageOfferDto, "stage" | "name">): CheckoutRequestInput {
  return {
    mode: "new",
    target: { kind: "HOME_UPGRADE", propertyId: home.propertyId, toStage: next.stage },
    subject: { title: `Upgrade to ${next.name}`, kind: "HOME_UPGRADE", propertyId: home.propertyId, address: home.address },
  };
}

export function itemCheckout(item: Pick<CatalogItemDto, "itemId" | "name" | "kind">): CheckoutRequestInput {
  return {
    mode: "new",
    target: { kind: "CATALOG_ITEM", itemId: item.itemId },
    subject: { title: `Buy ${item.name}`, kind: "CATALOG_ITEM", propertyId: null, itemName: item.name, itemKind: item.kind },
  };
}

export function resumeCheckout(orderId: string): CheckoutRequestInput {
  return { mode: "resume", orderId, subject: null };
}

/**
 * Rent a billboard for whole UTC days (M5): the board, the first day, the term and an APPROVED
 * billboard design. The server prices the term in the quote and books the days on fulfilment.
 */
export function billboardCheckout(input: {
  billboardId: string;
  label: string;
  startDate: string;
  endDate: string;
  termDays: 7 | 30;
  creativeId: string;
  creativeTitle: string;
}): CheckoutRequestInput {
  return {
    mode: "new",
    target: { kind: "BILLBOARD_RENTAL", billboardId: input.billboardId, startDate: input.startDate, termDays: input.termDays, creativeId: input.creativeId },
    subject: {
      title: `Rent ${input.label} for ${input.termDays} days`,
      kind: "BILLBOARD_RENTAL",
      propertyId: null,
      billboard: {
        billboardId: input.billboardId,
        label: input.label,
        startDate: input.startDate,
        endDate: input.endDate,
        termDays: input.termDays,
        creativeTitle: input.creativeTitle,
      },
    },
  };
}
